import {
  Box,
  Button,
  Text,
  VStack,
  HStack,
  Spinner,
  Divider,
} from "@chakra-ui/react";
import { FaSearch } from "react-icons/fa";
import WhiteBgInput from "../WhiteBgInput";
import AudienceList from "./AudienceList";
import { audienceTargetingKey } from "@common/utils/customAudienceTargetingKey";
import { upsertPageAudiencesByTargetingKey } from "@common/utils/upsertPageAudiencesByTargetingKey";
import SelectedAudiencesPanel from "./SelectedAudiencesPanel";
import { useFormContext } from "react-hook-form";
import {
  KYDAudienceData,
  CsvAudienceData,
  PageLookalikeAudienceData,
  PageEngagersAudienceData,
  AudienceData,
  InterestsAudienceData,
} from "@common/interfaces/KYDMarketing";
import {
  findInterestsAudienceIndexToReplace,
  isInterestsAudience,
} from "@common/utils/interestAudiences";
import {
  UseAudiencesState,
  UseAudiencesActions,
  useAudiences,
} from "../hooks/useAudiences";
import {
  CreateAudienceWizardDialogContext,
  CreateAudienceWizardDialogWrapper,
} from "./CreateAudienceWizard/CreateAudienceWizardDialog";
import CreateAudienceFromCsvDialog, {
  CreateAudienceFromCsvDialogContext,
} from "./CreateAudienceFromCsvDialog";
import {
  PageLookalikeAudienceDialog,
  PageLookalikeAudienceDialogContext,
} from "./PageLookalikeAudienceDialog";
import CreateMetaInterestsAudienceDialog, {
  CreateMetaInterestsAudienceDialogContext,
  EMPTY_INITIAL_INTERESTS,
  type InterestSearchResult,
} from "./CreateMetaInterestsAudienceDialog/CreateMetaInterestsAudienceDialog";
import { useDialogWithOptions, DialogProvider } from "src/Common/Dialog";
import {
  buildLookalikeName,
  PageLookalikeAudienceFormValues,
} from "./PageLookalikeAudienceDialog/types";
import { useEffect, useCallback, useMemo, useState } from "react";
import type { InstagramAccount } from "../types";

/** Unique key for audiences not yet backed by a Meta id (checkbox + list stability). */
function nextClientAudienceId(kind: string): string {
  return `${kind}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

const stableEmptyInterestSearch = async (): Promise<{
  data: InterestSearchResult[];
}> => ({ data: [] });

interface AudienceCardProps {
  fieldName?: string;
  loading?: {
    campaigns: boolean;
    audiences: boolean;
  };
  isEdit?: boolean;
  audiences: AudienceData[];
  pages?: Array<{
    id: string;
    name: string;
  }>;
  instagramAccounts?: InstagramAccount[];
  onFetchInstagramForFacebookPage?: (
    facebookPageId: string
  ) => void | Promise<InstagramAccount[]>;
  ad_account_id: string;
  isEmbedded?: boolean;
  title?: string;
  /** When true, Meta browse list appears only after a non-empty search; selected rows use the compact card layout. */
  searchOnlyBrowse?: boolean;
  searchInterests?: (
    query: string
  ) => Promise<{ data: InterestSearchResult[] }>;
};

type AudienceCardInnerProps = AudienceCardProps & {
  interestsDialogEdit: InterestsAudienceData | null;
  setInterestsDialogEdit: (audience: InterestsAudienceData | null) => void;
};

// Inner component that uses the dialog context
const AudienceCardInner = ({
  fieldName = "custom_audiences",
  loading,
  isEdit,
  pages = [],
  ad_account_id,
  instagramAccounts = [],
  onFetchInstagramForFacebookPage,
  isEmbedded = false,
  title = "Audiences",
  searchOnlyBrowse = false,
  searchInterests,
  interestsDialogEdit,
  setInterestsDialogEdit,
}: AudienceCardInnerProps) => {
  const { watch, setValue, register, control } = useFormContext();
  const [browseResultsActive, setBrowseResultsActive] = useState(false);
  const { open: openCreateAudienceWizard } = useDialogWithOptions(
    CreateAudienceWizardDialogContext
  );
  const { open: openCreateCsvAudienceDialog } = useDialogWithOptions(
    CreateAudienceFromCsvDialogContext
  );
  const { open: openPageLookalikeAudienceDialog } = useDialogWithOptions(
    PageLookalikeAudienceDialogContext
  );
  const { open: openMetaInterestsAudienceDialog } = useDialogWithOptions(
    CreateMetaInterestsAudienceDialogContext
  );

  const { state: audienceState, actions: audienceActions } = useAudiences();

  const audiencesLoading =
    loading?.audiences ?? audienceState.loading?.audiences ?? false;

  const runAudienceSearch = (query: string) => {
    const q = typeof query === "string" ? query.trim() : "";
    if (searchOnlyBrowse) {
      if (!q) {
        setBrowseResultsActive(false);
        return;
      }
      setBrowseResultsActive(true);
    }
    audienceActions.search({
      query: q,
      ad_account_id,
    });
  };

  useEffect(() => {
    if (!ad_account_id || searchOnlyBrowse) return;
    audienceActions.list({
      ad_account_id: ad_account_id,
      next: false,
    });
  }, [audienceActions.list, ad_account_id, searchOnlyBrowse]);

  const custom_audiences = watch(fieldName, []) as AudienceData[];
  // Get current audiences for display
  const currentCustomAudiences = useMemo(() => {
    return custom_audiences.map((audience) => {
      if (audience.audience_type === "kyd") {
        const metaAudience = audienceState.audiences?.find(
          (a) => a.external_id === audience.external_id
        );

        return {
          ...metaAudience,
          ...audience,
          id: audience.id,
          external_id: audience.external_id || metaAudience?.external_id,
          subtype: null,
        };
      }

      return audience;
    });
  }, [custom_audiences, audienceState.audiences]);

  const openCreateMetaInterestsDialog = useCallback(() => {
    setInterestsDialogEdit(null);
    openMetaInterestsAudienceDialog();
  }, [openMetaInterestsAudienceDialog]);

  const openEditMetaInterestsDialog = useCallback(
    (audience: InterestsAudienceData) => {
      setInterestsDialogEdit(audience);
      openMetaInterestsAudienceDialog();
    },
    [openMetaInterestsAudienceDialog]
  );

  // Handle custom audience creation - using watch inside callback to avoid stale closures
  const handleKydCustomAudienceCreated = useCallback(
    (audience: KYDAudienceData & { approximate_count_upper_bound?: number }) => {
      const currentAudiences = watch(fieldName, []) as AudienceData[];
      setValue(
        fieldName,
        [
          ...currentAudiences,
          {
            id: nextClientAudienceId("kyd_audience"),
            name: audience.name,
            audience_type: "kyd",
            subtype: "kyd",
            created_at: audience.created_at ?? new Date().toISOString(),
            segment_id: audience.segment_id,
            genres: audience.genres,
            artists: audience.artists,
            lookalike: audience.lookalike,
            ...(audience.segment_id &&
            audience.approximate_count_upper_bound != null
              ? {
                  approximate_count_upper_bound:
                    audience.approximate_count_upper_bound,
                }
              : {}),
          },
        ],
        {
          shouldDirty: true,
          shouldValidate: true,
        }
      );
    },
    [watch, setValue, fieldName]
  );

  // Handle CSV audience creation - using watch inside callback
  const handleCsvAudienceCreated = useCallback(
    (audienceData: {
      id: string;
      name: string;
      description: string;
      file_key: string;
      mapping: string[];
    }) => {
      const currentAudiences = watch(fieldName, []) as AudienceData[];
      const csvAudience: CsvAudienceData = {
        id: audienceData.id ?? nextClientAudienceId("csv_audience"),
        name: audienceData.name,
        description: audienceData.description,
        file_key: audienceData.file_key,
        mapping: audienceData.mapping,
        schema_fields: audienceData.mapping.filter((field) => field !== ""),
        audience_type: "csv",
        created_at: new Date().toISOString(),
      };
      setValue(fieldName, [...currentAudiences, csvAudience], {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [watch, setValue, fieldName]
  );

  // Handle page audience creation - handles both page_engagers and page_lookalike
  const handlePageLookalikeAudienceCreated = useCallback(
    (audienceData: PageLookalikeAudienceFormValues) => {
      const currentAudiences = watch(fieldName, []) as AudienceData[];
      const createdAt = new Date().toISOString();
      const selectedSources = [
        {
          pageType: "facebook" as const,
          pageId: String(audienceData.facebookPageId || "").trim(),
          pageName: String(audienceData.facebookPageName || "").trim(),
          suffix: "FB Page Audience",
          fallbackName: "Facebook",
          displayName: "Facebook",
        },
        {
          pageType: "instagram" as const,
          pageId: String(audienceData.instagramPageId || "").trim(),
          pageName: String(audienceData.instagramPageName || "").trim(),
          suffix: "IG Page Audience",
          fallbackName: "Instagram",
          displayName: "Instagram",
        },
      ].filter((source) => source.pageId);

      if (selectedSources.length === 0) {
        return;
      }

      if (audienceData.audienceMode === "engagers") {
        const nextPageEngagersAudiences: PageEngagersAudienceData[] =
          selectedSources.map((source) => ({
            id: nextClientAudienceId("page_engagers"),
            name: `${source.pageName || source.fallbackName} - ${
              source.suffix
            }`,
            description: `Page audience from ${source.pageName}`,
            page_type: source.pageType,
            page_id: source.pageId,
            page_name: source.pageName,
            audience_type: "page_engagers",
            created_at: createdAt,
          }));
        setValue(
          fieldName,
          upsertPageAudiencesByTargetingKey(
            currentAudiences,
            nextPageEngagersAudiences
          ),
          {
            shouldDirty: true,
            shouldValidate: true,
          }
        );
        return;
      }

      const ratio = audienceData.lookalike?.ratio || "0-1";
      const nextPageLookalikeAudiences: PageLookalikeAudienceData[] =
        selectedSources.map((source) => {
          const sourceLabel = `${source.pageName || source.fallbackName} - ${
            source.suffix
          }`;
          const lookalikeName = buildLookalikeName(sourceLabel, ratio);
          return {
            id: nextClientAudienceId("page_lookalike"),
            name: lookalikeName,
            description: `Lookalike audience from ${source.pageName} page`,
            page_type: source.pageType,
            page_id: source.pageId,
            page_name: source.pageName,
            lookalike: {
              name: lookalikeName,
              ratio,
            },
            audience_type: "page_lookalike",
            created_at: createdAt,
          };
        });

      setValue(
        fieldName,
        upsertPageAudiencesByTargetingKey(
          currentAudiences,
          nextPageLookalikeAudiences
        ),
        {
          shouldDirty: true,
          shouldValidate: true,
        }
      );
    },
    [watch, setValue, fieldName]
  );

  return (
    <>
      <VStack w="full" h="full" spacing={2}>
        <Box
          w="full"
          py={4}
          px={6}
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          backgroundColor="white"
        >
          <VStack align="flex-start" flex={1} spacing={4}>
            <HStack w="full" align="flex-start" justify="space-between" spacing={4}>
              <VStack flex={1} spacing={1} align="flex-start" minW={0}>
                <Text
                  fontWeight="bold"
                  fontSize="md"
                  color="gray.800"
                  lineHeight="1.2"
                >
                  Custom audience creation
                </Text>
                <Text fontSize="sm" color="gray.600" lineHeight="1.4">
                  Build from a CSV, KYD event data, or Facebook and Instagram
                  followers.
                </Text>
              </VStack>
              <Button
                flexShrink={0}
                bg="gray.900"
                color="white"
                size="sm"
                rounded="lg"
                px={3}
                py={2}
                fontSize="sm"
                fontWeight="medium"
                _hover={{ bg: "black" }}
                onClick={() => openCreateAudienceWizard()}
                data-testid="custom-audience-new-audience"
              >
                New Audience
              </Button>
            </HStack>
            <Divider borderColor={"gray.200"} />
            <HStack w="full" gap={2} align="stretch">
              <WhiteBgInput
                flex={1}
                lineHeight="23px"
                fontSize="14px"
                placeholder="Search for previously created audiences"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    runAudienceSearch(watch?.("audience_search_query") ?? "");
                  }
                }}
                {...(register ? register("audience_search_query") : {})}
              />
              <Button
                size="sm"
                h={10}
                variant="outline"
                leftIcon={
                  audiencesLoading ? <Spinner size="sm" /> : <FaSearch />
                }
                isDisabled={audiencesLoading}
                onClick={() => {
                  runAudienceSearch(watch?.("audience_search_query") ?? "");
                }}
              >
                Search
              </Button>
            </HStack>
            <VStack gap={3} w="full" align="stretch">
              {searchOnlyBrowse ? (
                <>
                  <SelectedAudiencesPanel
                    audiences={currentCustomAudiences || []}
                    control={control}
                    fieldName={fieldName}
                    onEditInterestsAudience={openEditMetaInterestsDialog}
                  />
                  {browseResultsActive ? (
                    <VStack align="stretch" spacing={2} w="full">
                      <Text fontWeight="semibold" fontSize="sm" color="gray.700">
                        Search results
                      </Text>
                      <AudienceList
                        audiences={
                          audienceState.audiences?.filter((a) => {
                            const metaKey = audienceTargetingKey(
                              a as AudienceData
                            );
                            if (!metaKey) return true;
                            return !currentCustomAudiences?.some(
                              (b) => audienceTargetingKey(b) === metaKey
                            );
                          }) || []
                        }
                        control={control}
                        fieldName={fieldName}
                      />
                    </VStack>
                  ) : null}
                </>
              ) : (
                <>
                  <AudienceList
                    audiences={currentCustomAudiences || []}
                    control={control}
                    fieldName={fieldName}
                  />
                  <AudienceList
                    audiences={
                      audienceState.audiences?.filter((a) => {
                        const metaKey = audienceTargetingKey(a as AudienceData);
                        if (!metaKey) return true;
                        return !currentCustomAudiences?.some(
                          (b) => audienceTargetingKey(b) === metaKey
                        );
                      }) || []
                    }
                    control={control}
                    fieldName={fieldName}
                  />
                </>
              )}
            </VStack>
          </VStack>
        </Box>
      </VStack>

      {/* Dialog Components */}
      <CreateAudienceWizardDialogWrapper
        onCreate={handleKydCustomAudienceCreated}
        onOpenCsv={() => openCreateCsvAudienceDialog()}
        onOpenFbIg={() => openPageLookalikeAudienceDialog()}
        onOpenMetaInterests={() => openCreateMetaInterestsDialog()}
      />
      <CreateAudienceFromCsvDialogWrapper onSubmit={handleCsvAudienceCreated} />
      <PageLookalikeAudienceDialogWrapper
        onCreate={handlePageLookalikeAudienceCreated}
        pages={pages}
        instagramAccounts={instagramAccounts}
        onFetchInstagramForFacebookPage={onFetchInstagramForFacebookPage}
      />
    </>
  );
};

const CreateAudienceFromCsvDialogWrapper = ({
  onSubmit,
}: {
  onSubmit: (audienceData: {
    id: string;
    name: string;
    description: string;
    file_key: string;
    mapping: string[];
  }) => void;
}) => {
  const { isOpen, close } = useDialogWithOptions(
    CreateAudienceFromCsvDialogContext
  );

  return (
    <CreateAudienceFromCsvDialog
      onSubmit={onSubmit}
      isOpen={isOpen}
      close={close}
    />
  );
};

// Wrapper for PageLookalikeAudienceDialog
const PageLookalikeAudienceDialogWrapper = ({
  onCreate,
  pages,
  instagramAccounts,
  onFetchInstagramForFacebookPage,
}: {
  onCreate: (audience: PageLookalikeAudienceFormValues) => void;
  pages?: Array<{
    id: string;
    name: string;
  }>;
  instagramAccounts?: InstagramAccount[];
  onFetchInstagramForFacebookPage?: (
    facebookPageId: string
  ) => void | Promise<InstagramAccount[]>;
}) => {
  const { isOpen, close } = useDialogWithOptions(
    PageLookalikeAudienceDialogContext
  );

  return (
    <PageLookalikeAudienceDialog
      onCreate={onCreate}
      isOpen={isOpen}
      close={close}
      pages={pages}
      instagramAccounts={instagramAccounts}
      onFetchInstagramForFacebookPage={onFetchInstagramForFacebookPage}
    />
  );
};

/** Holds interests-dialog state; modal is a sibling of Inner so form re-renders do not reset it. */
const AudienceCardWithMetaInterestsDialog = (props: AudienceCardProps) => {
  const [interestsDialogEdit, setInterestsDialogEdit] =
    useState<InterestsAudienceData | null>(null);
  const {
    isOpen: isMetaInterestsDialogOpen,
    close: closeMetaInterestsAudienceDialog,
  } = useDialogWithOptions(CreateMetaInterestsAudienceDialogContext);
  const { watch, setValue } = useFormContext();
  const fieldName = props.fieldName ?? "custom_audiences";

  const closeMetaInterestsDialog = useCallback(() => {
    setInterestsDialogEdit(null);
    closeMetaInterestsAudienceDialog();
  }, [closeMetaInterestsAudienceDialog]);

  const handleInterestsAudienceSubmit = useCallback(
    (audience: InterestsAudienceData) => {
      const currentAudiences = watch(fieldName, []) as AudienceData[];
      const editTarget = interestsDialogEdit;
      const nextRow: InterestsAudienceData = {
        ...audience,
        id:
          editTarget != null
            ? editTarget.id ??
              audience.id ??
              nextClientAudienceId("interests_audience")
            : nextClientAudienceId("interests_audience"),
        audience_type: "interests",
      };
      const replaceIndex =
        editTarget != null
          ? findInterestsAudienceIndexToReplace(currentAudiences, editTarget)
          : -1;
      if (replaceIndex >= 0) {
        const nextAudiences = [...currentAudiences];
        nextAudiences[replaceIndex] = nextRow;
        setValue(fieldName, nextAudiences, {
          shouldDirty: true,
          shouldValidate: true,
        });
      } else {
        setValue(fieldName, [...currentAudiences, nextRow], {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
      setValue("interests", [], { shouldDirty: true, shouldValidate: true });
      setInterestsDialogEdit(null);
      closeMetaInterestsAudienceDialog();
    },
    [
      watch,
      setValue,
      fieldName,
      interestsDialogEdit,
      closeMetaInterestsAudienceDialog,
    ]
  );

  return (
    <>
      <AudienceCardInner
        {...props}
        interestsDialogEdit={interestsDialogEdit}
        setInterestsDialogEdit={setInterestsDialogEdit}
      />
      <CreateMetaInterestsAudienceDialog
        isOpen={isMetaInterestsDialogOpen}
        close={closeMetaInterestsDialog}
        searchInterests={props.searchInterests ?? stableEmptyInterestSearch}
        onSubmit={handleInterestsAudienceSubmit}
        editAudienceId={interestsDialogEdit?.id ?? null}
        initialInterests={
          interestsDialogEdit?.interests ?? EMPTY_INITIAL_INTERESTS
        }
      />
    </>
  );
};

// Main component that provides dialog contexts
const AudienceCard = (props: AudienceCardProps) => {
  return (
    <DialogProvider context={CreateAudienceWizardDialogContext}>
      <DialogProvider context={CreateAudienceFromCsvDialogContext}>
        <DialogProvider context={PageLookalikeAudienceDialogContext}>
          <DialogProvider context={CreateMetaInterestsAudienceDialogContext}>
            <AudienceCardWithMetaInterestsDialog {...props} />
          </DialogProvider>
        </DialogProvider>
      </DialogProvider>
    </DialogProvider>
  );
};

export default AudienceCard;
